/**
 * Pure helper for translating raw AgencyGateway.status (and optional
 * health_status) into one of 4 UX buckets consumed by
 * <GatewayStatusBadge />.
 *
 * Mirrors the backend mapping at
 * backend/services/gateway_status.py:map_gateway_status — single source
 * of truth for the composite rule and bucket names. The frontend
 * re-derives on the client side so the UI can render the bucket
 * immediately without waiting for the backend's worst-status-wins
 * aggregation across multiple agencies.
 *
 * Plan 3 (assign-agency-to-resource) Task 7.
 */

/** Locked UX buckets. */
export type GatewayBucket = 'running' | 'provisioning' | 'degraded' | 'down';

/** Raw status → bucket mapping table (locked by spec). */
const STATUS_TO_BUCKET: Record<string, GatewayBucket> = {
  running: 'running',
  pending: 'provisioning',
  provisioning: 'provisioning',
  starting: 'provisioning',
  stopped: 'degraded',
  decommissioning: 'degraded',
  crashed: 'down',
  error: 'down',
  decommissioned: 'down',
};

/**
 * Map a raw AgencyGateway status to a 4-state UX bucket.
 *
 * Composite rule: status='running' + health_status='unhealthy' →
 * 'degraded'. Prevents a gateway that's up but failing health checks
 * from displaying as healthy.
 *
 * Also accepts a pre-computed bucket string directly (e.g.
 * `agency.gateway_status` from the admin Agencies list response) —
 * useful when the backend already aggregated worst-status-wins and
 * the frontend just wants to render the result without re-deriving.
 *
 * Unknown raw statuses return null (defensive — never throws).
 */
export function bucketFromStatus(
  raw: string | null | undefined,
  healthStatus: string | null | undefined = undefined,
): GatewayBucket | null {
  if (!raw) {
    return null;
  }
  // Pass-through if the caller already passed a 4-state bucket
  // (the backend's pre-computed gateway_status field on AgencyResponse).
  if (raw === 'running' || raw === 'provisioning' || raw === 'degraded' || raw === 'down') {
    // Composite rule still applies when the pre-computed bucket is 'running'
    // AND the caller supplied an explicit unhealthy health_status. This is
    // defensive — backends computing the bucket should already have applied
    // the rule. Frontend callers passing a pre-computed bucket typically
    // omit healthStatus, so the rule is a no-op for them.
    if (raw === 'running' && healthStatus === 'unhealthy') {
      return 'degraded';
    }
    return raw;
  }
  const bucket = STATUS_TO_BUCKET[raw];
  if (!bucket) {
    return null;
  }
  if (bucket === 'running' && healthStatus === 'unhealthy') {
    return 'degraded';
  }
  return bucket;
}

/**
 * Aggregate buckets across multiple gateway rows.
 *
 * Worst-status-wins per the precedence
 *   down > degraded > provisioning > running
 *
 * Used by the frontend to compute the bucket for a multi-agency admin
 * view (when the backend response doesn't already include the
 * pre-computed gateway_status). For agency-scoped views the backend's
 * 4-state bucket is consumed directly.
 */
export function worstBucket(
  pairs: Array<{ status: string | null | undefined; health_status?: string | null }>,
): GatewayBucket | null {
  const precedence: Record<GatewayBucket, number> = {
    running: 0,
    provisioning: 1,
    degraded: 2,
    down: 3,
  };
  let worst: GatewayBucket | null = null;
  let worstRank = -1;
  for (const { status, health_status } of pairs) {
    const bucket = bucketFromStatus(status, health_status);
    if (!bucket) continue;
    const rank = precedence[bucket];
    if (rank > worstRank) {
      worst = bucket;
      worstRank = rank;
    }
  }
  return worst;
}
